<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <BreadCrumb />
      </div>
    </div>

    <b-card>
      <div class="row d-flex mb-4 mb-xl-2 justify-content-between">
        <h4 class="col-12 col-xl-8 mb-2 font-weight-bold">權益卡列表</h4>
        <b-input-group class="col-12 col-xl-4 mb-2">
          <b-form-input v-model="query.keyword" placeholder="搜尋"></b-form-input>
          <b-input-group-append>
            <b-button @click="handlePageReset">
              <i class="fa fa-search"></i>
            </b-button>
          </b-input-group-append>
        </b-input-group>
      </div>
      <div class="d-flex mb-4 mb-xl-2 justify-content-between">
        <div
          class="
            d-flex
            flex-column flex-xl-row
            align-items-start align-items-xl-center
          "
        >
          <b-button
            v-if="checkPermission([consts.BENEFIT_CARD_CREATE])"
            class="flex-shrink-0 mb-2 mb-xl-0"
            variant="primary"
            :to="{ name: 'BenefitCardCreate' }"
          >
            <i class="fa fa-plus"></i>新增權益卡
          </b-button>
        </div>
      </div>
      <b-card-text>
        <div class="row">
          <div class="col-12">
            <b-overlay :show="showLoading" rounded="sm">
              <b-table
                striped
                hover
                responsive
                :items="benefitCards"
                :fields="fields"
              >
                <template #cell(main_image)="data">
                  <img
                    :src="deepGet(data.item, 'config.page_image')"
                    v-if="deepGet(data.item, 'config.page_image')"
                    class="thumb-image"
                    style="height: 50px; width: auto"
                  />
                </template>
                <template #cell(is_enabled)="data">
                  <b-badge
                    v-if="data.item.is_enabled == true"
                    variant="success"
                  >
                    開放
                  </b-badge>
                  <b-badge
                    v-if="data.item.is_enabled == false"
                    variant="secondary"
                  >
                    停用
                  </b-badge>
                </template>
                <template #cell(actions)="data">
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BENEFIT_CARD_MODIFY])"
                    variant="inverse-warning"
                    :to="{
                      name: 'BenefitCardEdit',
                      params: { id: data.item.id },
                    }"
                  >
                    <span class="mdi mdi-lead-pencil"></span> 編輯
                  </b-button>
                  <b-button
                    class="mr-1"
                    v-if="checkPermission([consts.BENEFIT_CARD_DELETE])"
                    variant="inverse-danger"
                    @click="deleteBenefitCard(data.item)"
                  >
                    <span class="mdi mdi-delete"></span> 刪除
                  </b-button>
                </template>
              </b-table>
            </b-overlay>
          </div>
        </div>
        <CustomPagination
          :currentPage="query.page"
          :totalRows="totalRows"
          :perPage="query.per_page"
          @page-change="handlePageChange"
          @per-page-change="handlePerPageChange"
        />
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import * as consts from "@/consts";
import PermissionChecker from "@/utils/PermissionChecker";
import { format } from "date-fns";
import benefitCardApi from "@/apis/benefitCard";
import _ from "lodash";
import { mapState } from "vuex";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import { paginationMixin } from "@/mixins/pagination";
import { updateQueryFromRoute, updateUrl } from "@/utils/updateUrl";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";

export default {
  components: { CustomPagination, BreadCrumb },
  mixins: [paginationMixin],
  data() {
    return {
      consts: consts,
      benefitCards: [],
      showLoading: false,
      fields: [
        { key: "title", label: "名稱", sortable: true },
        { key: "main_image", label: "主圖", sortable: false },
        { key: "is_enabled", label: "是否開放", sortable: false },
        { key: "order", label: "順序", sortable: true },
        {
          key: "start_at",
          label: "開始時間",
          sortable: true,
          formatter: (value) => {
            return format(new Date(value), "yyyy-MM-dd");
          },
        },
        {
          key: "end_at",
          label: "結束時間",
          sortable: true,
          formatter: (value) => {
            return format(new Date(value), "yyyy-MM-dd");
          },
        },
        {
          key: "created_at",
          label: "建立時間",
          sortable: true,
          formatter: (value) => {
            return format(new Date(value), "yyyy-MM-dd HH:mm:ss");
          },
        },
        { key: "actions", label: "管理" },
      ],
      totalRows: 0,
      initialized: false,
      query : {
        keyword: "",
        per_page: 20,
        page: 1,
      },
    };
  },
  computed: {
    ...mapState("general", {
      organization: (state) => state.organization,
    }),
    queryWatcher() {
      return `${this.query.page}-${this.query.per_page}`;
    },
  },
  watch: {
    queryWatcher: {
      handler() {
        if (!this.initialized) return;
        updateUrl(this.query, this.$store, this.$router);
      },
    },
    $route(to) {
      if (!this.initialized) return;
      this.query = { ...to.query };
      this.fetchBenefitCards();
    },
  },
  created() {
    this.query = updateQueryFromRoute(this.$route, this.$store, this.query);
  },
  async mounted() {
    await this.fetchBenefitCards();
    this.initialized = true;
  },
  methods: {
    deepGet: _.get,
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    async fetchBenefitCards() {
      try {
        this.showLoading = true;
        let params = { ...this.query };
        let response = await benefitCardApi.getBenefitCards(params);
        this.benefitCards = response.data.data;
        this.totalRows = response.data.meta.total;
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取列表失敗", "error");
      } finally {
        this.showLoading = false;
      }
    },
    async deleteBenefitCard(benefitCard) {
      try {
        this.showLoading = true;
        const result = await this.$swal({
          type: "warning",
          title: "您確定要刪除嗎？",
          html: `
            <div class="d-block">
              <div class="my-3">
                <div>刪除權益卡：${benefitCard.title}</div>
              </div>
            </div>`,
          confirmButtonText: "確定刪除",
          cancelButtonText: "不刪除",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          reverseButtons: true,
        });

        if (!result.value) {
          return;
        }

        await benefitCardApi.deleteBenefitCard(benefitCard.id);
        this.$swal("刪除成功", "", "success");
        await this.fetchBenefitCards();
      } catch (error) {
        console.error(error);
        this.$swal("刪除失敗", "", "error");
      } finally {
        this.showLoading = false;
      }
    },
  },
};
</script>
